import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { type RaRecord, useGetList, useGetMany, useNotify, useRecordContext, useRefresh } from "react-admin";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";

import { ShowAccordionSection } from "@/components/common/ShowAccordionSection";
import { getPartnerAwareApiUrl, httpClient } from "@/providers/dataProvider/dataProvider";
import { getResourceIcon } from "@/utils/icons";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { Policy, ResourceName, type VendorRunout } from "@/types";

/**
 * Associated records for a policy (US 5045 follow-up), grouped by type:
 * Client, Carrier, TPAs, Rx Vendors. Each type is one row; multiple records of
 * a type share the row as chips. Records are click-through links. Editing is
 * inline per type — a ＋ adds (array types) or ✎ changes (single types) via a
 * picker, and each chip has an ✕ to remove (array types). No mode toggle.
 */

const recordLabel = (record: RaRecord, field: string): string => {
  const value = (record as Record<string, unknown>)[field];
  return typeof value === "string" && value ? value : String(record.id);
};

interface LinkedRecordGroupProps {
  label: string;
  /** Currently linked record ids (one element for single-value types). */
  ids: string[];
  /** Canonical resource used to resolve display names + build show links. */
  resolveResource: ResourceName;
  /** Resource the picker lists options from (may be a filtered sub-endpoint). */
  optionResource: ResourceName;
  /** Field holding the display name (e.g. company_name, client_legal_name). */
  labelField: string;
  multiple: boolean;
  canEdit: boolean;
  /** Disables add/change/remove while a patch is in flight. */
  busy: boolean;
  onChange: (ids: string[]) => void;
  /** Enables the per-vendor run-out editor (#5325) — TPA/Rx groups only. */
  supportsRunout?: boolean;
  /** Current run-out entries for this group's vendors. */
  runout?: VendorRunout[];
  /** Persists the edited run-out entries (sent alongside the current ids). */
  onRunoutSave?: (runout: VendorRunout[]) => void;
}

const LinkedRecordGroup = ({
  label,
  ids,
  resolveResource,
  optionResource,
  labelField,
  multiple,
  canEdit,
  busy,
  onChange,
  supportsRunout = false,
  runout = [],
  onRunoutSave,
}: LinkedRecordGroupProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  // Server-side search for the add-picker (#5582) so records beyond the first page
  // are reachable — mirrors the q-filter pattern in InPlaceReferenceArrayEditor.
  const [searchText, setSearchText] = useState("");

  // Run-out editor (#5325): a popover listing the linked vendors with a run-out
  // checkbox + cutover date. Draft state is seeded from `runout` when opened.
  const [runoutAnchorEl, setRunoutAnchorEl] = useState<HTMLElement | null>(null);
  const runoutOpen = Boolean(runoutAnchorEl);
  const [draft, setDraft] = useState<Record<string, VendorRunout>>({});
  const runoutById = new Map(runout.map((r) => [r.vendor_id, r]));

  const openRunout = (e: React.MouseEvent<HTMLElement>) => {
    const seed: Record<string, VendorRunout> = {};
    runout.forEach((r) => (seed[r.vendor_id] = r));
    setDraft(seed);
    setRunoutAnchorEl(e.currentTarget);
  };
  const setDraftEntry = (vendorId: string, patch: Partial<VendorRunout>) =>
    setDraft((d) => {
      const base: VendorRunout = d[vendorId] ?? { vendor_id: vendorId, is_runout: false };
      return { ...d, [vendorId]: { ...base, ...patch } };
    });
  const saveRunout = () => {
    const entries = ids.map((id) => draft[id]).filter((r): r is VendorRunout => !!r && r.is_runout);
    onRunoutSave?.(entries);
    setRunoutAnchorEl(null);
  };

  const { data: linked = [] } = useGetMany(resolveResource, { ids }, { enabled: ids.length > 0 });
  const nameById = new Map(linked.map((r) => [String(r.id), recordLabel(r, labelField)]));

  // Picker options load only while the popover is open. Typing drives a server-side
  // `q` search so any record is reachable, not just the first page (#5582).
  const { data: options = [], isLoading: optionsLoading } = useGetList(
    optionResource,
    {
      // Server-side q search means we don't preload the full list; show a small
      // first page and let typing narrow it (per the list-fetching standard).
      pagination: { page: 1, perPage: 50 },
      sort: { field: labelField, order: "ASC" },
      filter: searchText ? { q: searchText } : {},
    },
    { enabled: open }
  );

  const Icon = getResourceIcon(resolveResource);

  const pick = (selectedId: string | undefined) => {
    setAnchorEl(null);
    setSearchText("");
    if (!selectedId) return;
    if (multiple) {
      if (!ids.includes(selectedId)) onChange([...ids, selectedId]);
    } else {
      onChange([selectedId]);
    }
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
        <Icon sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        {canEdit && (
          <Tooltip title={multiple ? `Add ${label}` : `Change ${label}`}>
            <IconButton
              size="small"
              sx={{ ml: "auto", p: 0.25 }}
              disabled={busy}
              aria-label={multiple ? `Add ${label}` : `Change ${label}`}
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              {multiple ? <AddIcon fontSize="small" /> : <EditIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        {supportsRunout && canEdit && ids.length > 0 && (
          <Tooltip title={`Edit run-out administrators`}>
            <IconButton
              size="small"
              sx={{ p: 0.25, ml: canEdit ? 0 : "auto" }}
              disabled={busy}
              aria-label={`Edit run-out for ${label}`}
              onClick={openRunout}
            >
              <HistoryToggleOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {ids.length === 0 ? (
        <Typography variant="body2" color="text.disabled">
          None
        </Typography>
      ) : (
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
          {ids.map((id) => (
            <Chip
              key={id}
              size="small"
              clickable
              onClick={() => navigate(`/${resolveResource}/${id}/show`)}
              label={`${nameById.get(id) ?? "…"}${runoutById.get(id)?.is_runout ? " (Run-out)" : ""}`}
              onDelete={canEdit && multiple && !busy ? () => onChange(ids.filter((x) => x !== id)) : undefined}
            />
          ))}
        </Stack>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setSearchText("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, width: 280 }}>
          <Autocomplete
            options={options}
            loading={optionsLoading}
            filterOptions={(x) => x}
            inputValue={searchText}
            onInputChange={(_, value, reason) => {
              // Track user-driven changes (typing + the clear ✗ button); ignore the
              // programmatic "reset" MUI fires after a pick so it can't reinstate a
              // stale label on reopen (pick/onClose already clear searchText).
              if (reason === "input" || reason === "clear") setSearchText(value);
            }}
            getOptionLabel={(o) => recordLabel(o, labelField)}
            getOptionDisabled={(o) => multiple && ids.includes(String(o.id))}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            noOptionsText={searchText ? "No matches found" : "Type to search…"}
            onChange={(_, value) => pick(value ? String(value.id) : undefined)}
            renderInput={(params) => (
              <TextField {...params} autoFocus size="small" placeholder={`Search ${label.replace(/s$/, "")}`} />
            )}
          />
        </Box>
      </Popover>

      <Popover
        open={runoutOpen}
        anchorEl={runoutAnchorEl}
        onClose={() => setRunoutAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, width: 340 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Run-out administrators
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {ids.map((id) => {
              const entry = draft[id];
              const isRunout = entry?.is_runout ?? false;
              return (
                <Box key={id}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={isRunout}
                        onChange={(e) => setDraftEntry(id, { is_runout: e.target.checked })}
                      />
                    }
                    label={<Typography variant="body2">{nameById.get(id) ?? "…"}</Typography>}
                  />
                  {isRunout && (
                    <Box sx={{ pl: 3.5, pb: 0.5 }}>
                      <TextField
                        type="date"
                        size="small"
                        fullWidth
                        label="Cutover date"
                        InputLabelProps={{ shrink: true }}
                        sx={{ mt: 0.5 }}
                        value={entry?.runout_cutover_date ?? ""}
                        onChange={(e) => setDraftEntry(id, { runout_cutover_date: e.target.value || undefined })}
                      />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <Button size="small" variant="contained" disabled={busy} onClick={saveRunout}>
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

interface LinkedOverrides {
  client_id_ref?: string | null;
  vendor_id_ref?: string | null;
  tpa_vendor_ids?: string[];
  rx_vendor_ids?: string[];
  tpa_vendor_runout?: VendorRunout[];
  rx_vendor_runout?: VendorRunout[];
}

export const PolicyLinkedRecordsSection = () => {
  const policy = useRecordContext<Policy>();
  const { canEdit } = useResourcePermissions(ResourceName.POLICIES);
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();
  const [isPatching, setIsPatching] = useState(false);

  if (!policy) return null;

  // PATCH directly (the data provider uses PUT for policies, which requires a
  // full body). Send ONLY the changed field: PatchPolicy treats an omitted
  // junction array as a no-op (nil = skip), and single refs are pointers, so
  // there's no need to echo the other fields — which also avoids overwriting
  // them from a stale cached record.
  const patchLinked = async (overrides: LinkedOverrides) => {
    setIsPatching(true);
    try {
      await httpClient(`${getPartnerAwareApiUrl()}/policies/${policy.id}`, {
        method: "PATCH",
        body: JSON.stringify(overrides),
      });
      notify("Policy updated", { type: "success" });
      refresh();
    } catch (error) {
      notifyError(error);
    } finally {
      setIsPatching(false);
    }
  };

  return (
    <ShowAccordionSection label="Linked Records" defaultExpanded>
      <LinkedRecordGroup
        label="Client"
        ids={policy.client_id_ref ? [policy.client_id_ref] : []}
        resolveResource={ResourceName.CLIENTS}
        optionResource={ResourceName.CLIENTS}
        labelField="client_legal_name"
        multiple={false}
        canEdit={canEdit}
        busy={isPatching}
        onChange={(ids) => patchLinked({ client_id_ref: ids[0] ?? null })}
      />
      <LinkedRecordGroup
        label="Carrier"
        ids={policy.vendor_id_ref ? [policy.vendor_id_ref] : []}
        resolveResource={ResourceName.VENDORS}
        optionResource={ResourceName.VENDORS_CARRIERS}
        labelField="company_name"
        multiple={false}
        canEdit={canEdit}
        busy={isPatching}
        onChange={(ids) => patchLinked({ vendor_id_ref: ids[0] ?? null })}
      />
      <LinkedRecordGroup
        label="TPAs"
        ids={policy.tpa_vendor_ids ?? []}
        resolveResource={ResourceName.VENDORS}
        optionResource={ResourceName.VENDORS_TPAS}
        labelField="company_name"
        multiple
        canEdit={canEdit}
        busy={isPatching}
        onChange={(ids) => patchLinked({ tpa_vendor_ids: ids })}
        supportsRunout
        runout={policy.tpa_vendor_runout ?? []}
        onRunoutSave={(ro) => patchLinked({ tpa_vendor_ids: policy.tpa_vendor_ids ?? [], tpa_vendor_runout: ro })}
      />
      <LinkedRecordGroup
        label="Rx Vendors"
        ids={policy.rx_vendor_ids ?? []}
        resolveResource={ResourceName.VENDORS}
        optionResource={ResourceName.VENDORS_RX}
        labelField="company_name"
        multiple
        canEdit={canEdit}
        busy={isPatching}
        onChange={(ids) => patchLinked({ rx_vendor_ids: ids })}
        supportsRunout
        runout={policy.rx_vendor_runout ?? []}
        onRunoutSave={(ro) => patchLinked({ rx_vendor_ids: policy.rx_vendor_ids ?? [], rx_vendor_runout: ro })}
      />
    </ShowAccordionSection>
  );
};
